import { useState, useEffect } from "react";
import { PlanLimitDialog, usePlanLimitHandler } from "../components/PlanUsage";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import Layout from "../components/Layout";
import { todayLocalISO } from "../lib/utils";
import { SortableHeader, toggleSort, applySortFn, getPersistedSort } from "../components/SortableHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { 
  Plus, 
  Search, 
  DollarSign,
  ChevronRight,
  Filter,
  Trash2,
  Upload,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";

const Employees = () => {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [payGroups, setPayGroups] = useState([]);
  const [scheduleGroups, setScheduleGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sort, setSort] = useState(() => getPersistedSort("employees", "name"));

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    sms_opt_in: false,
    hire_date: todayLocalISO(),
    pay_type: "hourly",
    pay_value: "",
    pay_group_id: "",
    schedule_group_id: "",
    pin: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [empRes, pgRes, sgRes] = await Promise.all([
        apiClient.get(`/employees${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`),
        apiClient.get('/pay-groups'),
        apiClient.get('/schedule-groups')
      ]);
      setEmployees(empRes.data.employees);
      setPayGroups(pgRes.data.pay_groups);
      setScheduleGroups(sgRes.data.schedule_groups || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const employeeLimit = usePlanLimitHandler("employee");

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Enforce required linkage — every employee must belong to a Pay Group
    // AND a Schedule Group so downstream flows (scheduling board, payroll,
    // balance calc) work correctly.
    const hasEmail = Boolean(formData.email?.trim());
    const hasPhone = Boolean(formData.phone?.trim());
    if (!hasEmail && !hasPhone) {
      toast.error("Add an email or a phone number so this employee can sign in.");
      return;
    }
    if (hasPhone && !hasEmail && !formData.sms_opt_in) {
      toast.error("For a phone-only employee, confirm the SMS opt-in checkbox (TCPA requirement).");
      return;
    }
    if (!formData.pay_group_id) {
      toast.error("Pay Group is required. Create one from Pay Groups if you haven't yet.");
      return;
    }
    if (!formData.schedule_group_id) {
      toast.error("Schedule Group is required. Create one from Shifts → Groups if you haven't yet.");
      return;
    }
    setSaving(true);
    const outcome = await employeeLimit.run(() =>
      apiClient.post('/employees', {
        ...formData,
        pay_value: parseFloat(formData.pay_value),
        pin: formData.pin || undefined
      })
    );
    if (outcome.ok) {
      setShowAddDialog(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        sms_opt_in: false,
        hire_date: todayLocalISO(),
        pay_type: "hourly",
        pay_value: "",
        pay_group_id: "",
        schedule_group_id: "",
        pin: "",
        notes: ""
      });
      fetchData();
    } else if (outcome.blocked) {
      // Close the "Add Employee" dialog so the limit dialog isn't stacked underneath.
      setShowAddDialog(false);
    } else {
      console.error("Failed to create employee:", outcome.error);
    }
    setSaving(false);
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchQuery.toLowerCase())
  ).map(e => ({ ...e, _balance: e.balance?.balance || 0 })).sort(applySortFn(sort));

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getPayTypeLabel = (type) => {
    const labels = {
      hourly: "Hourly",
      salaried: "Salaried",
      daily: "Daily Rate"
    };
    return labels[type] || type;
  };

  const getPayGroupName = (id) => {
    const group = payGroups.find(g => g.pay_group_id === id);
    return group?.group_name || "-";
  };

  const handleDeleteEmployee = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/employees/${deleteTarget.employee_id}`);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete employee");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <div data-testid="employees-page" className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">
              Employees
            </h1>
            <p className="text-slate-600 mt-1">
              Manage your workforce and pay structures
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/settings?tab=import')}
              data-testid="import-employees-btn"
              className="border-slate-300"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#0D9488] hover:bg-[#14B8A6]"
                  data-testid="add-employee-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Employee</DialogTitle>
                <DialogDescription>
                  Enter the employee&apos;s information and pay details.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Doe"
                      required
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email <span className="text-slate-400 text-xs">(or phone below)</span></Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                      data-testid="employee-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone <span className="text-slate-400 text-xs">(required for phone sign-in)</span></Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(555) 010-0000"
                      data-testid="employee-phone-input"
                    />
                    {formData.phone && (
                      <label
                        htmlFor="sms-opt-in"
                        className="flex items-start gap-2 text-xs text-slate-600 leading-snug pt-1 cursor-pointer"
                        data-testid="employee-sms-optin-label"
                      >
                        <input
                          id="sms-opt-in"
                          type="checkbox"
                          checked={formData.sms_opt_in}
                          onChange={(e) => setFormData({...formData, sms_opt_in: e.target.checked})}
                          className="mt-0.5 accent-[#0D9488]"
                          data-testid="employee-sms-optin-input"
                        />
                        <span>
                          I confirm this employee has agreed to receive SMS messages from Nirvanta for login verification and important workplace updates. Standard message rates may apply. Reply STOP to opt out.
                        </span>
                      </label>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hire_date">Hire Date *</Label>
                    <Input
                      id="hire_date"
                      type="date"
                      value={formData.hire_date}
                      onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                      required
                      data-testid="employee-hiredate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pay_group">Pay Group *</Label>
                    <Select 
                      value={formData.pay_group_id} 
                      onValueChange={(val) => setFormData({...formData, pay_group_id: val})}
                      required
                    >
                      <SelectTrigger data-testid="employee-paygroup-select">
                        <SelectValue placeholder={payGroups.length ? "Select pay group" : "Create a pay group first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {payGroups.map(pg => (
                          <SelectItem key={pg.pay_group_id} value={pg.pay_group_id}>
                            {pg.group_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {payGroups.length === 0 && (
                      <p className="text-xs text-amber-600">
                        <button type="button" onClick={() => navigate('/pay-groups')} className="underline hover:text-amber-700" data-testid="create-paygroup-link">Create a Pay Group</button> before adding employees.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="schedule_group">Schedule Group *</Label>
                    <Select 
                      value={formData.schedule_group_id} 
                      onValueChange={(val) => setFormData({...formData, schedule_group_id: val})}
                      required
                    >
                      <SelectTrigger data-testid="employee-schedulegroup-select">
                        <SelectValue placeholder={scheduleGroups.length ? "Select schedule group" : "Create a schedule group first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {scheduleGroups.map(sg => (
                          <SelectItem key={sg.group_id} value={sg.group_id}>
                            {sg.group_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {scheduleGroups.length === 0 && (
                      <p className="text-xs text-amber-600">
                        <button type="button" onClick={() => navigate('/shifts?tab=groups')} className="underline hover:text-amber-700" data-testid="create-schedulegroup-link">Create a Schedule Group</button> before adding employees.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pay_type">Pay Type *</Label>
                    <Select 
                      value={formData.pay_type} 
                      onValueChange={(val) => setFormData({...formData, pay_type: val})}
                    >
                      <SelectTrigger data-testid="employee-paytype-select">
                        <SelectValue placeholder="Select pay type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="salaried">Salaried (Monthly)</SelectItem>
                        <SelectItem value="daily">Daily Rate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pay_value">
                      {formData.pay_type === 'hourly' ? 'Hourly Rate *' : 
                       formData.pay_type === 'salaried' ? 'Monthly Salary *' : 'Daily Rate *'}
                    </Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="pay_value"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.pay_value}
                        onChange={(e) => setFormData({...formData, pay_value: e.target.value})}
                        placeholder="0.00"
                        className="pl-9"
                        required
                        data-testid="employee-payvalue-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pin">Time Clock PIN <span className="text-slate-400 text-xs">(4-6 digits)</span></Label>
                    <Input
                      id="pin"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={formData.pin}
                      onChange={(e) => setFormData({...formData, pin: e.target.value.replace(/\D/g, "").slice(0, 6)})}
                      placeholder="4-6 digit PIN"
                      data-testid="employee-pin-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Any additional notes..."
                      rows={2}
                      data-testid="employee-notes-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving}
                    className="bg-[#0D9488] hover:bg-[#14B8A6]"
                    data-testid="save-employee-btn"
                  >
                    {saving ? "Saving..." : "Add Employee"}
                  </Button>
                </div>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="employee-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[150px]" data-testid="status-filter-select">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="auto_archived">Auto-archived (plan downgrade)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Employee Table */}
        <Card className="border border-slate-200">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead><SortableHeader label="Employee" sortKey="name" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "employees"))} /></TableHead>
                  <TableHead><SortableHeader label="Pay Group" sortKey="pay_group_id" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "employees"))} /></TableHead>
                  <TableHead><SortableHeader label="Pay Type" sortKey="pay_type" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "employees"))} /></TableHead>
                  <TableHead><SortableHeader label="Rate" sortKey="pay_value" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "employees"))} /></TableHead>
                  <TableHead><SortableHeader label="Balance" sortKey="_balance" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "employees"))} /></TableHead>
                  <TableHead><SortableHeader label="Status" sortKey="status" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "employees"))} /></TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <div className="h-12 bg-slate-100 rounded animate-pulse"></div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      No employees found. Add your first employee to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((emp) => (
                    <TableRow 
                      key={emp.employee_id} 
                      className="cursor-pointer table-row-hover"
                      onClick={() => navigate(`/employees/${emp.employee_id}`)}
                      data-testid="employee-table-row"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={emp.avatar} alt={emp.name} />
                            <AvatarFallback className="bg-[#0D9488] text-white">
                              {emp.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-[#0A192F]">{emp.name}</p>
                            <p className="text-sm text-slate-500">{emp.email || emp.phone || "-"}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {getPayGroupName(emp.pay_group_id)}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {getPayTypeLabel(emp.pay_type)}
                      </TableCell>
                      <TableCell className="font-medium text-[#0A192F]">
                        {formatCurrency(emp.pay_value)}
                        <span className="text-xs text-slate-500 ml-1">
                          {emp.pay_type === 'hourly' ? '/hr' : emp.pay_type === 'daily' ? '/day' : '/mo'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`font-semibold ${
                          emp.balance?.balance > 0 ? 'text-[#E11D48]' : 
                          emp.balance?.balance < 0 ? 'text-[#10B981]' : 'text-slate-600'
                        }`}>
                          {formatCurrency(emp.balance?.balance || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={emp.status === 'active' ? 'default' : 'secondary'}
                          className={emp.status === 'active' ? 'bg-[#10B981] hover:bg-[#10B981]' : ''}
                        >
                          {emp.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(emp)}
                            data-testid={`delete-employee-${emp.employee_id}`}
                            title="Delete employee"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                          {tenant?.whatsapp_enabled && tenant?.plan !== "free" && emp.phone && (
                            <a
                              href={`https://wa.me/${emp.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${emp.name.split(" ")[0]}, this is ${tenant?.company_name || "your manager"} — `)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-slate-100 text-emerald-600"
                              title="Message on WhatsApp"
                              data-testid={`whatsapp-${emp.employee_id}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </a>
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-400 cursor-pointer" onClick={() => navigate(`/employees/${emp.employee_id}`)} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#E11D48]">Delete Employee</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <span className="font-semibold text-[#0A192F]">{deleteTarget?.name}</span>?
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-900 space-y-1.5">
              <p className="font-medium">This permanently removes:</p>
              <ul className="list-disc list-inside space-y-0.5 text-[13px] leading-snug">
                <li>All time entries, payments, and pay-rate history</li>
                <li>Their schedules and availability</li>
                <li>Their sign-in account &amp; active sessions <span className="text-rose-600 text-xs">(if they had one)</span></li>
              </ul>
              <p className="text-[12px] text-rose-700 pt-1">
                They can create a new account on their own afterward. This cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-btn">Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteEmployee} disabled={deleting} data-testid="confirm-delete-employee-btn">
                <Trash2 className="w-4 h-4 mr-2" /> {deleting ? "Deleting..." : "Delete Employee"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <PlanLimitDialog {...employeeLimit.dialogProps} />
      </div>
    </Layout>
  );
};

export default Employees;
